import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';

const TypingAnimation = ({ 
  texts, 
  typingSpeed = 80, 
  pauseDuration = 2000, 
  fadeOutDuration = 500,
  sx = {},
  variant = "h4",
  ...props 
}) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  // HTML 태그를 제거한 순수 텍스트 길이를 계산하는 함수
  const getPlainTextLength = (htmlText) => {
    return htmlText.replace(/<[^>]*>/g, '').length;
  };

  // 현재 인덱스까지의 HTML을 반환하는 함수
  const getDisplayTextUpToIndex = (htmlText, targetIndex) => {
    let plainTextIndex = 0;
    let result = '';
    let i = 0;
    
    while (i < htmlText.length && plainTextIndex < targetIndex) {
      if (htmlText[i] === '<') {
        // HTML 태그 시작
        let tagEnd = htmlText.indexOf('>', i);
        if (tagEnd !== -1) {
          result += htmlText.substring(i, tagEnd + 1);
          i = tagEnd + 1;
        } else {
          i++;
        }
      } else {
        // 일반 텍스트
        result += htmlText[i];
        plainTextIndex++;
        i++;
      }
    }
    
    return result;
  };

  useEffect(() => {
    if (!texts || texts.length === 0) return;

    const currentText = texts[currentTextIndex];
    const plainTextLength = getPlainTextLength(currentText);
    let timeoutId;

    if (isTyping && currentCharIndex < plainTextLength) {
      // 타이핑 중
      timeoutId = setTimeout(() => {
        const newDisplayText = getDisplayTextUpToIndex(currentText, currentCharIndex + 1);
        setDisplayedText(newDisplayText);
        setCurrentCharIndex(prev => prev + 1);
      }, typingSpeed);
    } else if (isTyping && currentCharIndex >= plainTextLength) {
      // 타이핑 완료, 잠시 대기
      timeoutId = setTimeout(() => {
        setIsTyping(false);
        // 페이드 아웃 시작
        setIsVisible(false);
      }, pauseDuration);
    } else if (!isTyping && !isVisible) {
      // 페이드 아웃 완료 후 다음 텍스트로 전환
      timeoutId = setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % texts.length);
        setDisplayedText('');
        setCurrentCharIndex(0);
        setIsTyping(true);
        setIsVisible(true);
      }, fadeOutDuration);
    }

    return () => clearTimeout(timeoutId);
  }, [currentTextIndex, currentCharIndex, isTyping, isVisible, texts, typingSpeed, pauseDuration, fadeOutDuration]);

  if (!texts || texts.length === 0) return null;

  return (
    <Box
      sx={{
        transition: `opacity ${fadeOutDuration}ms ease-in-out`,
        opacity: isVisible ? 1 : 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start', // 상단 정렬로 변경
        minHeight: { xs: '100px', md: '140px' }, // 고정 높이로 레이아웃 안정성 확보
        paddingTop: { xs: '10px', md: '20px' }, // 상단 여백으로 위치 조정
      }}
    >
      <Typography
        variant={variant}
        {...props}
        sx={{
          position: 'relative',
          width: '100%',
          textAlign: 'center',
          '&::after': {
            content: '"|"',
            animation: 'blink 1s infinite',
            marginLeft: '2px',
            color: '#1976d2',
            fontWeight: 'normal'
          },
          '@keyframes blink': {
            '0%, 50%': { opacity: 1 },
            '51%, 100%': { opacity: 0 }
          },
          ...sx // 전달받은 sx를 마지막에 적용하여 우선순위 보장
        }}
        dangerouslySetInnerHTML={{ __html: displayedText }}
      />
    </Box>
  );
};

export default TypingAnimation; 